import { ReactNode } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, ButtonBase, Radio, Skeleton } from "@mui/material"

const useStyles = makeStyles()((theme) => ({
    cardBase: {
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        padding: "1rem 1rem 1rem 1.25rem",
        borderRadius: ".5rem",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
    },
    card: {
        width: "100%",
        justifyContent: "flex-start",
        textAlign: "left",
        cursor: "pointer",
        background: theme.palette.common.white,
        transition: "border-color 0.15s, background 0.15s",
        "&:hover": {
            background: theme.palette.tailwind.slate[50],
        },
        "&:focus-visible": {
            outline: `.125rem solid ${theme.palette.primary.main}`,
            outlineOffset: ".125rem",
        },
    },
    cardSelected: {
        borderColor: theme.palette.primary.main,
        background: theme.palette.common.white,
        "&:hover": {
            background: theme.palette.common.white,
        },
    },
    cardDisabled: {
        opacity: 0.45,
        cursor: "not-allowed",
        "&:hover": {
            background: theme.palette.common.white,
        },
    },
    radio: {
        padding: 0,
        flexShrink: 0,
        color: theme.palette.text.secondary,
        "&.Mui-checked": {
            color: theme.palette.primary.main,
        },
    },
    content: {
        flex: 1,
        minWidth: 0,
    },
    endAdornment: {
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
    },
}))

type SelectableRadioCardProps = Readonly<{
    isSelected: boolean
    isDisabled?: boolean
    onSelect: () => void
    children: ReactNode
    endAdornment?: ReactNode
}>

export function SelectableRadioCard({
    isSelected,
    isDisabled = false,
    onSelect,
    children,
    endAdornment,
}: SelectableRadioCardProps) {
    const { classes, cx } = useStyles()

    return (
        <ButtonBase
            className={cx(classes.cardBase, classes.card, {
                [classes.cardSelected]: isSelected,
                [classes.cardDisabled]: isDisabled,
            })}
            onClick={onSelect}
            disabled={isDisabled}
            disableRipple
        >
            <Radio checked={isSelected} disabled={isDisabled} tabIndex={-1} disableRipple className={classes.radio} />
            <Box className={classes.content}>{children}</Box>
            {endAdornment && <Box className={classes.endAdornment}>{endAdornment}</Box>}
        </ButtonBase>
    )
}

type SelectableRadioCardSkeletonProps = Readonly<{
    hasEndAdornment?: boolean
}>

export function SelectableRadioCardSkeleton({ hasEndAdornment = false }: SelectableRadioCardSkeletonProps) {
    const { classes } = useStyles()

    return (
        <Box className={classes.cardBase}>
            <Skeleton variant="circular" animation="wave" width="1.5rem" height="1.5rem" />
            <Box className={classes.content}>
                <Skeleton variant="text" animation="wave" width="50%" height="1.5rem" />
            </Box>
            {hasEndAdornment && <Skeleton variant="rounded" animation="wave" width="2.25rem" height="2.25rem" />}
        </Box>
    )
}
